import { omit, sortBy } from 'lodash';
import { RESET_ALL as RESET_ON_LOGOUT, danger } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import {
  trackEventCampaignInviteAccepted,
  trackEventCampaignInviteRejected,
  trackEventCampaignInviteSent
} from 'modules/tracking/utils/tracking';

import { createReducer } from 'utils/redux';
import { API, post, put } from 'utils/api';
import {
  SUBCAMPAIGN_STATE_ACCEPTED,
  SUBCAMPAIGN_STATE_DECLINED,
  SUBCAMPAIGN_STATE_PENDING
} from 'utils/subcampaign';
import { __t, applyTranslations } from 'utils/language';
import { isDeliverableNew } from 'utils/deliverables';
import { logError } from 'utils/log';

applyTranslations(require('../components/subcampaign-offer/subcampaign-offer.i18n.json'));

import type {
  Action,
  Deliverable,
  SubcampaignOfferForm,
  SubcampaignOfferState,
  ThunkAction
} from 'types';

const LOADING = 'offer/LOADING';
const LOADED = 'offer/LOADED';
const RESET = 'offer/RESET';

const initialState = {
  isLoading: false
};

const MSG_KEY = 'subcampaign_offer_module';

function setLoadingStatus(status: boolean): Action {
  return {
    type: LOADING,
    payload: {
      status
    }
  };
}

export function putOffer(offer, deliverables) {
  return async (dispatch) => {
    dispatch(setLoadingStatus(true));

    const updatedDeliverables = deliverables.map((deliverable) => {
      const updatedDeliverable = {
        ...deliverable
      };

      if (isDeliverableNew(deliverable)) {
        return omit(updatedDeliverable, ['id', 'offerId']);
      }

      return {
        ...deliverable,
        ...updatedDeliverable
      };
    });

    const updatedAdditionalCompensation = offer.type === 'other'
      ? { voucherAmount: null, voucherDescription: '', otherDescription: '' }
      : offer.additionalCompensation;

    try {
      const offerWithDeliverables = {
        ...offer,
        additionalCompensation: updatedAdditionalCompensation,
        deliverables: updatedDeliverables
      };

      await put(API.END_POINTS.OFFERS.offer(offer.id),
        { data: offerWithDeliverables },
        { suppressGlobalNotification: true }
      );
      dispatch(setLoadingStatus(false));
    } catch (error) {
      const errorMsgByCode = __t(`${MSG_KEY}.errors.${error?.response?.data?.code}`);
      const errorMsg = errorMsgByCode || __t(`${MSG_KEY}.errors.offer_put`);

      dispatch(danger(errorMsg, { keepBetweenPages: true }));
      dispatch(setLoadingStatus(false));

      throw error;
    }
  };
}

export function saveOffersAsDraft(
  offers: Array<SubcampaignOfferForm>,
  deliverables: Array<Deliverable>
): ThunkAction {
  return async dispatch => {
    const loaderType = 'sequence-of-draft-offers';

    try {
      dispatch(showLoading(loaderType));

      await offers.reduce<Promise<void>>((prev, off) => (
        prev.then(() => dispatch(putOffer(off, deliverables)))
      ), Promise.resolve());


      dispatch(hideLoading(loaderType));
    } catch (error) {
      dispatch(hideLoading(loaderType));
      logError(error);
    }
  };
}

export function putOfferState(offerId: number, offerState: SubcampaignOfferState) {
  const url = API.END_POINTS.OFFERS.putOfferState(offerId);
  return async (dispatch) => {
    dispatch(showLoading(url));
    dispatch(setLoadingStatus(true));

    try {
      await put(url, { data: offerState });
      dispatch(setLoadingStatus(false));

      if (offerState.state === SUBCAMPAIGN_STATE_PENDING) {
        trackEventCampaignInviteSent(offerId);
      } else if (offerState.state === SUBCAMPAIGN_STATE_ACCEPTED) {
        trackEventCampaignInviteAccepted(offerId);
      } else if (offerState.state === SUBCAMPAIGN_STATE_DECLINED) {
        trackEventCampaignInviteRejected(offerId);
      }

      dispatch(hideLoading(url));
    } catch (error) {
      if (error?.response?.status === 409) {
        dispatch(danger(__t(`${MSG_KEY}.errors.offer_has_conflict`)));
      }

      dispatch(setLoadingStatus(false));
      dispatch(hideLoading(url));
      throw error;
    }
  };
}

export function postSubcampaignOfferWithDeliverables(subcampaignId, oldOffer, editedOffer, oldDeliverables, editedDeliverables) {
  const loaderId = 'postSubcampaignOfferWithDeliverables()';

  return async (dispatch) => {
    dispatch(setLoadingStatus(true));
    dispatch(showLoading(loaderId));

    try {
      const sortedDeliverables = sortBy(editedDeliverables, 'id');
      const offerToSend = {
        ...editedOffer,
        deliverables: sortedDeliverables
      };
      const newOffer = await post(API.END_POINTS.SUBCAMPAIGNS.createSubcampaignOffer(subcampaignId), { data: offerToSend });
      await dispatch(putOfferState(newOffer.data.id, { state: SUBCAMPAIGN_STATE_PENDING }));
      dispatch(setLoadingStatus(false));
      dispatch(hideLoading(loaderId));
    } catch (error) {
      dispatch(setLoadingStatus(false));
      dispatch(hideLoading(loaderId));
      throw error;
    }

    return Promise.resolve();
  };
}

export default createReducer<any, any>('subcampaignOffer', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [LOADED]: (state) => ({
    ...state,
    isLoading: false
  })
});
